import React, { useMemo, useEffect } from 'react';
import { MovementType, PaymentMethod, UserRole, UserType, ServiceCategory } from '../../types/index';
import { X, Package, DollarSign, Wrench, ArrowLeft, FileText, ReceiptText, ArrowLeftRight, Check, Plus, Trash2, Calendar, Clock, Wind, Sun } from 'lucide-react';
import { construirPayloadMovimento, calcularTotal } from './logicaMovimentos';
import { calcularTroco } from '../../shared/logica/pagamentos';
import SelectMetodoPagamento from './SelectMetodoPagamento';
import LinhasPagamentoMisto from './LinhasPagamentoMisto';
import TrocoCash from './TrocoCash';
import { UNIQUE_COUNTRIES } from '../../utils/countries';

// Chave para guardar a última categoria de serviço escolhida
const LAST_SERVICE_CATEGORY_KEY = 'lastServiceCategory';

/**
 * Modal para criar novo movimento (grande, com 2 steps)
 */
export default function ModalCriarMovimento({
  isOpen,
  onClose,
  form,
  onFormChange,
  createStep,
  onCreateStepChange,
  docType,
  onDocTypeChange,
  saleType,
  onSaleTypeChange,
  isMixedPayment,
  onIsMixedPaymentChange,
  deliveryStatus,
  onDeliveryStatusChange,
  valueReceived,
  onValueReceivedChange,
  matchedClientId,
  isEditing = false,
  onSubmit,
  currentUser,
  countryCode,
  onCountryCodeChange,
  isSimpleUser = false,
  techJobs = [],
  budgets = [],
  editingMovement = null
}) {

  const selectMovementType = (type) => {
    onFormChange(prev => ({ ...prev, type }));
    onCreateStepChange('FORM');
  };

  // Carregar última categoria escolhida quando o tipo é SERVIÇO e é Fatura/Recibo
  useEffect(() => {
    // Apenas carregar se não estiver a editar, for SERVIÇO, for Fatura/Recibo e não tiver categoria definida
    if (!isEditing && form.type === MovementType.SERVICO && docType === 'INVOICE_RECEIPT' && !form.serviceCategory) {
      try {
        const lastCategory = localStorage.getItem(LAST_SERVICE_CATEGORY_KEY);
        if (lastCategory && (lastCategory === ServiceCategory.AR_CONDICIONADO || 
                            lastCategory === ServiceCategory.PAINEL_SOLAR || 
                            lastCategory === ServiceCategory.OUTROS)) {
          onFormChange(prev => ({ ...prev, serviceCategory: lastCategory }));
        }
      } catch (error) {
        console.warn('Não foi possível carregar a preferência de categoria:', error);
      }
    }
  }, [form.type, docType, isEditing, form.serviceCategory, onFormChange]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar localização (obrigatório)
    if (!form.locationType) {
      alert('Por favor, selecione se a venda foi "Fora" ou "Na Loja".');
      return;
    }
    
    // Validar categoria de serviço (obrigatório para serviços com Fatura/Recibo)
    // Verificar se é string vazia, null, undefined ou não existe
    if (form.type === MovementType.SERVICO && docType === 'INVOICE_RECEIPT') {
      const hasServiceCategory = form.serviceCategory && 
                                 typeof form.serviceCategory === 'string' && 
                                 form.serviceCategory.trim() !== '';
      if (!hasServiceCategory) {
        alert('Por favor, selecione o tipo de serviço (Ar Condicionado / Painel Solar ou Outros). A categoria é obrigatória para serviços com Fatura/Recibo.');
        return;
      }
    }
    
    // Validar que há pelo menos um produto com nome
    if (!form.products || form.products.length === 0 || !form.products.some(p => p.name.trim())) {
      alert('Por favor, adicione pelo menos um produto com nome.');
      return;
    }
    
    // Guardar a última categoria escolhida no localStorage (apenas para serviços)
    if (form.type === MovementType.SERVICO && form.serviceCategory) {
      try {
        localStorage.setItem(LAST_SERVICE_CATEGORY_KEY, form.serviceCategory);
      } catch (error) {
        console.warn('Não foi possível guardar a preferência de categoria:', error);
      }
    }
    
    const payload = construirPayloadMovimento(form, saleType, isMixedPayment, deliveryStatus, currentUser?.id, countryCode || '351');
    onSubmit(payload);
    onClose();
  };

  const totalValue = useMemo(() => calcularTotal(form, isMixedPayment), [form, isMixedPayment]);

  const changeToReturn = useMemo(() => calcularTroco(valueReceived, totalValue), [valueReceived, totalValue]);

  const isAdminOrOwner = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.OWNER;
  const isEmployeeComplexEditing = isEditing && currentUser?.role === UserRole.EMPLOYEE && (currentUser?.userType === UserType.COMPLEX || !currentUser?.userType);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[70] flex items-center justify-center p-4">
      <div className={`bg-white ${isSimpleUser ? 'rounded-3xl w-full max-w-sm' : 'rounded-[2.5rem] w-full max-w-3xl'} shadow-2xl max-h-[95vh] overflow-y-auto`}>
        {/* Header */}
        <div className={`flex items-center justify-between ${isSimpleUser ? 'px-5 py-4' : 'px-8 py-6'} border-b border-slate-100`}>
          <div className="flex items-center gap-3">
            {createStep === 'FORM' && (
              <button
                type="button"
                onClick={() => onCreateStepChange('PICK_TYPE')}
                className="p-2 rounded-full bg-slate-50 hover:bg-slate-100 transition-colors"
                title="Voltar"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className={`${isSimpleUser ? 'text-xl' : 'text-2xl'} font-black text-slate-900`}>
              {createStep === 'PICK_TYPE'
                ? (isEditing ? 'Editar Registo' : 'Novo Registo')
                : `${isEditing ? 'Editar' : 'Novo'} Registo • ${form.type === MovementType.VENDA ? 'Venda' : form.type === MovementType.GAS ? 'Gás' : 'Serviço'}`}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full bg-slate-50 hover:bg-slate-100 transition-colors">
            <X />
          </button>
        </div>

        {/* Step 1: escolher tipo */}
        {createStep === 'PICK_TYPE' && (
          <div className={`${isSimpleUser ? 'p-6' : 'p-10'}`}>
            {isSimpleUser ? (
              // Layout para usuário simples: 2 em cima, 1 em baixo, centralizado
              <div className="flex flex-col items-center justify-center gap-5 py-4">
                {/* Linha superior: 2 botões */}
                <div className="grid grid-cols-2 gap-3 w-full max-w-[280px]">
                  <button
                    type="button"
                    onClick={() => selectMovementType(MovementType.VENDA)}
                    className="rounded-2xl border-2 border-slate-200 bg-white hover:bg-slate-50 hover:border-emerald-300 transition-all active:scale-[0.96] p-4 flex flex-col items-center gap-2.5"
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                      <Package size={20} />
                    </div>
                    <span className="font-black tracking-wide text-slate-900 text-xs">VENDA</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => selectMovementType(MovementType.GAS)}
                    className="rounded-2xl border-2 border-slate-200 bg-white hover:bg-slate-50 hover:border-blue-300 transition-all active:scale-[0.96] p-4 flex flex-col items-center gap-2.5"
                  >
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                      <DollarSign size={20} />
                    </div>
                    <span className="font-black tracking-wide text-slate-900 text-xs">GÁS</span>
                  </button>
                </div>
                {/* Linha inferior: 1 botão centralizado */}
                <div className="w-full flex justify-center">
                  <button
                    type="button"
                    onClick={() => selectMovementType(MovementType.SERVICO)}
                    className="rounded-2xl border-2 border-slate-200 bg-white hover:bg-slate-50 hover:border-amber-300 transition-all active:scale-[0.96] p-4 flex flex-col items-center gap-2.5 w-full max-w-[180px]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                      <Wrench size={20} />
                    </div>
                    <span className="font-black tracking-wide text-slate-900 text-xs">SERVIÇO</span>
                  </button>
                </div>
              </div>
            ) : (
              // Layout original para usuários complexos
              <div className="grid grid-cols-3 gap-6">
                <button
                  type="button"
                  onClick={() => selectMovementType(MovementType.VENDA)}
                  className="rounded-3xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors p-8 flex flex-col items-center gap-4"
                >
                  <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                    <Package size={34} />
                  </div>
                  <span className="font-black tracking-widest text-slate-900">VENDA</span>
                </button>
                <button
                  type="button"
                  onClick={() => selectMovementType(MovementType.GAS)}
                  className="rounded-3xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors p-8 flex flex-col items-center gap-4"
                >
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600">
                    <DollarSign size={34} />
                  </div>
                  <span className="font-black tracking-widest text-slate-900">GÁS</span>
                </button>
                <button
                  type="button"
                  onClick={() => selectMovementType(MovementType.SERVICO)}
                  className="rounded-3xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors p-8 flex flex-col items-center gap-4"
                >
                  <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600">
                    <Wrench size={34} />
                  </div>
                  <span className="font-black tracking-widest text-slate-900">SERVIÇO</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: formulário */}
        {createStep === 'FORM' && (
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Tabs documento - empregado complexo não pode alterar */}
            <div className={`bg-slate-50 rounded-2xl p-2 flex gap-2 ${isEmployeeComplexEditing ? 'pointer-events-none bg-slate-200' : ''}`}>
              <button
                type="button"
                onClick={() => !isEmployeeComplexEditing && onDocTypeChange('SIMPLE_RECEIPT')}
                className={`flex-1 rounded-xl px-4 py-3 font-black text-sm flex items-center justify-center gap-2 transition-colors ${
                  docType === 'SIMPLE_RECEIPT' ? 'bg-white text-green-800 shadow' : 'text-slate-400'
                }`}
              >
                <ReceiptText size={18} /> Recibo Simples
              </button>
              <button
                type="button"
                onClick={() => !isEmployeeComplexEditing && onDocTypeChange('INVOICE_RECEIPT')}
                className={`flex-1 rounded-xl px-4 py-3 font-black text-sm flex items-center justify-center gap-2 transition-colors ${
                  docType === 'INVOICE_RECEIPT' ? 'bg-white text-green-800 shadow' : 'text-slate-400'
                }`}
              >
                <FileText size={18} /> Fatura / Recibo
              </button>
            </div>

            {/* Seleção de Localização */}
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">
                Localização *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => onFormChange(prev => ({ ...prev, locationType: 'NA_LOJA' }))}
                  className={`p-4 rounded-2xl border-2 transition-all font-black text-sm ${
                    form.locationType === 'NA_LOJA'
                      ? 'bg-green-600 border-green-600 text-white shadow-md'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-green-300'
                  }`}
                >
                  Na Loja
                </button>
                <button
                  type="button"
                  onClick={() => onFormChange(prev => ({ ...prev, locationType: 'FORA' }))}
                  className={`p-4 rounded-2xl border-2 transition-all font-black text-sm ${
                    form.locationType === 'FORA'
                      ? 'bg-green-600 border-green-600 text-white shadow-md'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-green-300'
                  }`}
                >
                  Fora
                </button>
              </div>
            </div>

            {/* Categoria de Serviço - empregado complexo não pode alterar */}
            {form.type === MovementType.SERVICO && docType === 'INVOICE_RECEIPT' && (
              <div className={isEmployeeComplexEditing ? 'pointer-events-none bg-slate-200 rounded-2xl p-4' : ''}>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-2">
                  Tipo de Serviço *
                  {(!form.serviceCategory || (typeof form.serviceCategory === 'string' && form.serviceCategory.trim() === '')) && (
                    <span className="text-red-600 normal-case ml-2">(Obrigatório)</span>
                  )}
                </label>
                {(!form.serviceCategory || (typeof form.serviceCategory === 'string' && form.serviceCategory.trim() === '')) && (
                  <div className="mb-3 p-3 bg-amber-50 border-2 border-amber-200 rounded-xl text-sm text-amber-800 font-bold">
                    ⚠️ Por favor, selecione uma categoria de serviço antes de guardar.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => onFormChange(prev => ({ ...prev, serviceCategory: ServiceCategory.AR_CONDICIONADO }))}
                    className={`p-4 rounded-2xl border-2 transition-all font-black text-sm flex flex-col items-center gap-2 ${
                      form.serviceCategory === ServiceCategory.AR_CONDICIONADO || form.serviceCategory === ServiceCategory.PAINEL_SOLAR
                        ? 'bg-green-600 border-green-600 text-white shadow-md'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Wind size={20} />
                      <Sun size={20} />
                    </div>
                    <span>Ar Condicionado / Painel Solar</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onFormChange(prev => ({ ...prev, serviceCategory: ServiceCategory.OUTROS }))}
                    className={`p-4 rounded-2xl border-2 transition-all font-black text-sm flex flex-col items-center gap-2 ${
                      form.serviceCategory === ServiceCategory.OUTROS
                        ? 'bg-slate-600 border-slate-600 text-white shadow-md'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <Wrench size={20} />
                    <span>Outros</span>
                  </button>
                </div>
              </div>
            )}

            {/* Campo de Data - Só aparece ao editar */}
            {isEditing && (
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2 flex items-center gap-2">
                  <Calendar size={14} />
                  Data do Movimento *
                </label>
                <input
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => onFormChange(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full p-4 bg-white border-2 border-slate-100 rounded-2xl font-black text-lg outline-none focus:border-green-600"
                />
              </div>
            )}

            {/* Seleção de Assistência Técnica ou Orçamento - empregado complexo não pode alterar */}
            {form.type === MovementType.SERVICO && (
              <div className={`bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-3xl p-6 ${isEmployeeComplexEditing ? 'pointer-events-none bg-slate-200 border-slate-300' : ''}`}>
                <div className="text-[10px] font-black text-green-800 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Wrench size={14} /> Vincular a Assistência Técnica ou Orçamento
                </div>
                
                {/* Selecionar tipo: Assistência Técnica ou Orçamento */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => onFormChange(prev => ({ 
                      ...prev, 
                      relatedType: prev.relatedType === 'TECH' ? '' : 'TECH',
                      relatedBudgetId: '' // Limpar orçamento se selecionar assistência
                    }))}
                    className={`p-4 rounded-2xl border-2 transition-all font-black text-sm ${
                      form.relatedType === 'TECH'
                        ? 'bg-green-600 border-green-600 text-white shadow-md'
                        : 'bg-white border-green-200 text-green-700 hover:border-green-400'
                    }`}
                  >
                    <Wrench size={20} className="mx-auto mb-2" />
                    Assistência Técnica
                  </button>
                  <button
                    type="button"
                    onClick={() => onFormChange(prev => ({ 
                      ...prev, 
                      relatedType: prev.relatedType === 'BUDGET' ? '' : 'BUDGET',
                      relatedTechJobId: '' // Limpar assistência se selecionar orçamento
                    }))}
                    className={`p-4 rounded-2xl border-2 transition-all font-black text-sm ${
                      form.relatedType === 'BUDGET'
                        ? 'bg-green-600 border-green-600 text-white shadow-md'
                        : 'bg-white border-green-200 text-green-700 hover:border-green-400'
                    }`}
                  >
                    <FileText size={20} className="mx-auto mb-2" />
                    Orçamento
                  </button>
                </div>

                {/* Lista de Assistências Técnicas - apenas não concluídas ou não pagas */}
                {form.relatedType === 'TECH' && (
                  <div>
                    <label className="text-[10px] font-black text-green-800 uppercase tracking-widest block mb-2">
                      Selecionar Assistência Técnica
                    </label>
                    <select
                      value={form.relatedTechJobId || ''}
                      onChange={(e) => onFormChange(prev => ({ ...prev, relatedTechJobId: e.target.value }))}
                      className="w-full p-4 bg-white border-2 border-green-200 rounded-2xl font-black outline-none focus:border-green-600"
                    >
                      <option value="">— Selecionar Assistência Técnica —</option>
                      {techJobs
                        .filter(tj => !tj.isArchived && (tj.status !== 'CONCLUIDA' || !tj.isPaid))
                        .map(tj => (
                          <option key={tj.id} value={tj.id}>
                            {tj.client} - {tj.locality || ''} {tj.date ? `(${tj.date})` : ''} - {tj.status || 'ABERTA'}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {/* Lista de Orçamentos - apenas pendentes ou aprovados */}
                {form.relatedType === 'BUDGET' && (
                  <div>
                    <label className="text-[10px] font-black text-green-800 uppercase tracking-widest block mb-2">
                      Selecionar Orçamento
                    </label>
                    <select
                      value={form.relatedBudgetId || ''}
                      onChange={(e) => onFormChange(prev => ({ ...prev, relatedBudgetId: e.target.value }))}
                      className="w-full p-4 bg-white border-2 border-green-200 rounded-2xl font-black outline-none focus:border-green-600"
                    >
                      <option value="">— Selecionar Orçamento —</option>
                      {budgets
                        .filter(b => b.status === 'PENDENTE' || b.status === 'APROVADO')
                        .map(b => (
                          <option key={b.id} value={b.id}>
                            {b.client || b.location || '—'} - {(b.total || 0).toFixed(2)}€ - {b.status} {b.date ? `(${b.date})` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Produtos - empregado complexo: não alterar/remover existentes, só acrescentar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Produtos / Serviços *</div>
                <button
                  type="button"
                  onClick={() => onFormChange(prev => ({
                    ...prev,
                    products: [...(prev.products || []), { name: '', ref: '' }]
                  }))}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-xl text-xs font-black hover:bg-green-700 transition-colors"
                >
                  <Plus size={14} /> Adicionar Produto
                </button>
              </div>
              
              {(!form.products || form.products.length === 0) ? (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-sm text-amber-800 font-bold">
                  Adicione pelo menos um produto ou serviço
                </div>
              ) : (
                <div className="space-y-3">
                  {form.products.map((prod, index) => {
                    const originalCount = editingMovement?.products?.length ?? (editingMovement?.product ? 1 : 0);
                    const isOriginalProduct = isEmployeeComplexEditing && index < originalCount;
                    return (
                    <div key={index} className="bg-white border-2 border-slate-100 rounded-2xl p-4 flex gap-3 items-start">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Nome do Produto *</div>
                          <input
                            required
                            value={prod.name}
                            onChange={(e) => !isOriginalProduct && onFormChange(prev => ({
                              ...prev,
                              products: prev.products.map((p, i) => 
                                i === index ? { ...p, name: e.target.value } : p
                              )
                            }))}
                            readOnly={isOriginalProduct}
                            className={`w-full p-3 rounded-xl font-black text-sm outline-none border border-slate-200 ${isOriginalProduct ? 'bg-slate-200 border-slate-300 text-slate-700 cursor-not-allowed' : 'bg-slate-50'}`}
                            placeholder={form.type === MovementType.GAS ? 'Ex: Botija 13kg' : 'Ex: Reparação placa'}
                          />
                        </div>
                        <div>
                          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Referência</div>
                          <input
                            value={prod.ref}
                            onChange={(e) => !isOriginalProduct && onFormChange(prev => ({
                              ...prev,
                              products: prev.products.map((p, i) => 
                                i === index ? { ...p, ref: e.target.value } : p
                              )
                            }))}
                            readOnly={isOriginalProduct}
                            className={`w-full p-3 rounded-xl font-black text-sm outline-none border border-slate-200 ${isOriginalProduct ? 'bg-slate-200 border-slate-300 text-slate-700 cursor-not-allowed' : 'bg-slate-50'}`}
                            placeholder="Ex: REF123"
                          />
                        </div>
                      </div>
                      {form.products.length > 1 && !isOriginalProduct && (
                        <button
                          type="button"
                          onClick={() => onFormChange(prev => ({
                            ...prev,
                            products: prev.products.filter((_, i) => i !== index)
                          }))}
                          className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors mt-6"
                          title="Remover produto"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                      {form.products.length > 1 && isOriginalProduct && <div className="w-10" />}
                    </div>
                  );})}
                </div>
              )}
              
              {/* Campo de compatibilidade (oculto mas mantido para não quebrar) */}
              <input
                type="hidden"
                value={form.products && form.products.length > 0 
                  ? form.products.map(p => p.ref ? `${p.name} (Ref: ${p.ref})` : p.name).join(', ')
                  : form.product}
                onChange={() => {}}
              />
            </div>

            {/* Linha valor total + pagamento misto - empregado complexo: valor readonly, misto bloqueado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Valor Total (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={isMixedPayment ? totalValue.toFixed(2) : form.value}
                  onChange={(e) => !isEmployeeComplexEditing && onFormChange(prev => ({ ...prev, value: e.target.value }))}
                  className={`w-full p-4 border-2 border-slate-100 rounded-2xl font-black text-lg outline-none focus:border-green-600 ${isEmployeeComplexEditing ? 'bg-slate-200 border-slate-300 text-slate-700 cursor-not-allowed' : 'bg-white'}`}
                  disabled={isMixedPayment || isEmployeeComplexEditing}
                />
              </div>
              <button
                type="button"
                onClick={() => !isEmployeeComplexEditing && onIsMixedPaymentChange(!isMixedPayment)}
                disabled={isEmployeeComplexEditing}
                className={`w-full p-4 border-2 border-slate-100 rounded-2xl font-black flex items-center justify-center gap-2 text-slate-600 ${isEmployeeComplexEditing ? 'bg-slate-200 border-slate-300 text-slate-600 cursor-not-allowed' : 'bg-white hover:bg-slate-50'}`}
              >
                <ArrowLeftRight size={20} /> {isMixedPayment ? 'Desativar Pagamento Misto' : 'Ativar Pagamento Misto'}
              </button>
            </div>

            {/* Método de Pagamento - empregado complexo: só paymentMethod2 editável (pagamento misto) */}
            {!isMixedPayment ? (
              <>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Método de Pagamento *</label>
                  <SelectMetodoPagamento
                    value={form.paymentMethod}
                    onChange={(val) => onFormChange(prev => ({ ...prev, paymentMethod: val }))}
                    disabled={false}
                  />
                </div>
                {/* Campo de Data - apenas em edição e quando o método é Multibanco (sem pagamento misto) */}
                {isEditing && form.paymentMethod === PaymentMethod.CARD && (
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2 flex items-center gap-2">
                      <Calendar size={14} />
                      Data Pagamento (Multibanco)
                    </label>
                    <input
                      type="date"
                      value={form.paymentDate1}
                      onChange={(e) => onFormChange(prev => ({ ...prev, paymentDate1: e.target.value }))}
                      className="w-full p-3 bg-white border-2 border-slate-100 rounded-xl font-black text-sm outline-none focus:border-green-600"
                    />
                  </div>
                )}
                {/* Campo de Hora - só aparece ao editar, se for ADMIN/OWNER e se o método de pagamento for Multibanco (sem pagamento misto) */}
                {isEditing && isAdminOrOwner && form.paymentMethod === PaymentMethod.CARD && (
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2 flex items-center gap-2">
                      <Clock size={14} />
                      Hora Pagamento (Multibanco)
                    </label>
                    <input
                      type="time"
                      value={form.paymentTime1 || ''}
                      onChange={(e) => onFormChange(prev => ({ ...prev, paymentTime1: e.target.value }))}
                      className="w-full p-3 bg-white border-2 border-slate-100 rounded-xl font-black text-sm outline-none focus:border-green-600"
                    />
                  </div>
                )}
              </>
            ) : (
              <LinhasPagamentoMisto
                payments={form.payments || []}
                onPaymentsChange={(payments) => onFormChange(prev => ({ ...prev, payments }))}
                isEditing={isEditing}
                isAdminOrOwner={isAdminOrOwner}
                isEmployeeComplexEditing={isEmployeeComplexEditing}
              />
            )}

            {/* Valor Recebido (só aparece quando não é pagamento misto e método é CASH) */}
            {!isMixedPayment && form.paymentMethod === PaymentMethod.CASH && (
              <TrocoCash
                valorRecebido={valueReceived}
                troco={changeToReturn}
                onValorRecebidoChange={onValueReceivedChange}
              />
            )}

            {/* Cliente (só em Fatura/Recibo) - empregado complexo não pode alterar dados do cliente */}
            {docType === 'INVOICE_RECEIPT' && (
              <div className={`bg-white border-2 border-slate-100 rounded-3xl p-6 space-y-4 ${isEmployeeComplexEditing ? 'pointer-events-none bg-slate-200 border-slate-300' : ''}`}>
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cliente</div>

                {/* Toggle Button: Cliente / Consumidor Final */}
                <div className="bg-slate-50 rounded-2xl border border-slate-200 shadow-sm p-1.5 sm:p-1">
                  <div className="grid grid-cols-2 gap-2 sm:gap-1 relative">
                    <button
                      type="button"
                      onClick={() => {
                        if (!isEmployeeComplexEditing) {
                          onSaleTypeChange('CLIENT');
                          onFormChange(prev => ({ ...prev, client: '' }));
                        }
                      }}
                      className={`py-5 px-4 sm:py-4 font-black text-base sm:text-sm rounded-xl transition-all relative z-10 active:scale-[0.98] min-h-[56px] sm:min-h-0 ${
                        saleType === 'CLIENT'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-500 active:text-slate-700'
                      }`}
                    >
                      Cliente
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isEmployeeComplexEditing) {
                          onSaleTypeChange('SIMPLE');
                          onFormChange(prev => ({ ...prev, client: 'Consumidor Final' }));
                        }
                      }}
                      className={`py-5 px-3 sm:py-4 sm:px-4 font-black text-base sm:text-sm rounded-xl transition-all relative z-10 active:scale-[0.98] min-h-[56px] sm:min-h-0 ${
                        saleType === 'SIMPLE'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-500 active:text-slate-700'
                      }`}
                    >
                      Consumidor Final
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Telefone (lookup inteligente)</div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <select
                          value={countryCode || '351'}
                          onChange={(e) => onCountryCodeChange(e.target.value)}
                          className="w-full p-4 bg-slate-50 rounded-2xl font-black text-xs outline-none border border-slate-100"
                          disabled={!isEditing && saleType !== 'CLIENT'}
                        >
                          {UNIQUE_COUNTRIES.map(country => (
                            <option key={country.code} value={country.code}>
                              {country.flag} +{country.code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-8">
                        <input
                          value={form.phone}
                          onChange={(e) => onFormChange(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                          placeholder={countryCode === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                          disabled={!isEditing && saleType !== 'CLIENT'}
                        />
                      </div>
                    </div>
                    {saleType === 'CLIENT' && matchedClientId && (
                      <div className="mt-2 text-[10px] font-black text-emerald-700 uppercase tracking-widest">
                        Cliente encontrado • dados preenchidos
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nome do Cliente *</div>
                    <input
                      value={saleType === 'SIMPLE' && !isEditing ? 'Consumidor Final' : form.client}
                      onChange={(e) => onFormChange(prev => ({ ...prev, client: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                      disabled={!isEditing && saleType === 'SIMPLE'}
                      required={saleType === 'CLIENT' || isEditing}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Morada / Rua</div>
                    <input
                      value={form.street}
                      onChange={(e) => onFormChange(prev => ({ ...prev, street: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nº Porta</div>
                    <input
                      value={form.doorNumber}
                      onChange={(e) => onFormChange(prev => ({ ...prev, doorNumber: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Localidade</div>
                    <input
                      value={form.locality}
                      onChange={(e) => onFormChange(prev => ({ ...prev, locality: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Cód. Postal</div>
                    <input
                      value={form.zipCode}
                      onChange={(e) => onFormChange(prev => ({ ...prev, zipCode: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">NIF</div>
                    <input
                      value={form.nif}
                      onChange={(e) => onFormChange(prev => ({ ...prev, nif: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">E-mail <span className="text-[9px] normal-case font-normal">(opcional)</span></div>
                    <input
                      type="email"
                      value={form.email || ''}
                      onChange={(e) => onFormChange(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none border border-slate-100"
                      placeholder="exemplo@email.com"
                    />
                  </div>
                </div>

                {/* Faturação em outro nome - só quando for Cliente (não Consumidor Final) */}
                {saleType === 'CLIENT' && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => onFormChange(prev => ({ ...prev, useDifferentBilling: !prev.useDifferentBilling }))}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-colors ${
                        form.useDifferentBilling ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                          form.useDifferentBilling ? 'bg-emerald-600 border-emerald-600' : 'border-slate-200 bg-white'
                        }`}>
                          {form.useDifferentBilling && <Check size={16} className="text-white" />}
                        </div>
                        <span className="font-black text-slate-800">Faturação em outro nome?</span>
                      </div>
                      <span className="text-xs font-black text-slate-400">
                        {form.useDifferentBilling ? 'Sim' : 'Não'}
                      </span>
                    </button>
                    {form.useDifferentBilling && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50/80 rounded-2xl border border-slate-100">
                        <div className="md:col-span-2">
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nome para faturação *</div>
                          <input
                            value={form.billingClient || ''}
                            onChange={(e) => onFormChange(prev => ({ ...prev, billingClient: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-black outline-none border border-slate-100"
                            placeholder="Nome ou empresa"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Morada / Rua</div>
                          <input
                            value={form.billingStreet || ''}
                            onChange={(e) => onFormChange(prev => ({ ...prev, billingStreet: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-black outline-none border border-slate-100"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nº Porta</div>
                          <input
                            value={form.billingDoorNumber || ''}
                            onChange={(e) => onFormChange(prev => ({ ...prev, billingDoorNumber: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-black outline-none border border-slate-100"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Localidade</div>
                          <input
                            value={form.billingLocality || ''}
                            onChange={(e) => onFormChange(prev => ({ ...prev, billingLocality: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-black outline-none border border-slate-100"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Cód. Postal</div>
                          <input
                            value={form.billingZipCode || ''}
                            onChange={(e) => onFormChange(prev => ({ ...prev, billingZipCode: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-black outline-none border border-slate-100"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">NIF</div>
                          <input
                            value={form.billingNif || ''}
                            onChange={(e) => onFormChange(prev => ({ ...prev, billingNif: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-black outline-none border border-slate-100"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Entrega */}
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      // Se não tem status, definir como PENDING (Para Entregar) por padrão
                      if (!deliveryStatus) {
                        onDeliveryStatusChange('PENDING');
                      } else {
                        // Se já tem status, ao clicar novamente desmarcar
                        onDeliveryStatusChange(null);
                      }
                    }}
                    className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-colors ${
                      deliveryStatus ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                        deliveryStatus ? 'bg-emerald-600 border-emerald-600' : 'border-slate-200 bg-white'
                      }`}>
                        {deliveryStatus && <Check size={16} className="text-white" />}
                      </div>
                      <span className="font-black text-slate-800">Entrega?</span>
                    </div>
                    <span className="text-xs font-black text-slate-400">
                      {deliveryStatus === 'PENDING' ? 'Para entregar' : deliveryStatus === 'DELIVERED' ? 'Entregue' : 'Não'}
                    </span>
                  </button>
                  
                  {/* Botões de status de entrega - aparecem quando tem status definido */}
                  {deliveryStatus && (
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          onDeliveryStatusChange('PENDING');
                        }}
                        className={`p-4 rounded-2xl border-2 transition-colors font-black text-sm ${
                          deliveryStatus === 'PENDING' 
                            ? 'bg-blue-50 border-blue-300 text-blue-700' 
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-200'
                        }`}
                      >
                        Para Entregar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onDeliveryStatusChange('DELIVERED');
                        }}
                        className={`p-4 rounded-2xl border-2 transition-colors font-black text-sm ${
                          deliveryStatus === 'DELIVERED' 
                            ? 'bg-green-50 border-green-300 text-green-700' 
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-green-50 hover:border-green-200'
                        }`}
                      >
                        Entregue
                      </button>
                    </div>
                  )}
                </div>
                {/* Notas / Observações internas */}
                <div className="space-y-2 mt-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-2">
                    Notas / Observações
                  </label>
                  <textarea
                    value={form.notes || ''}
                    onChange={(e) => onFormChange(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full min-h-[80px] p-4 bg-slate-50 rounded-2xl font-medium text-sm outline-none border border-slate-100 focus:border-green-600"
                    placeholder="Ex: combinado entregar amanhã de manhã; atenção ao acesso ao prédio; prefere contacto por SMS..."
                  />
                </div>
              </div>
            )}

            {/* Controlo de faturação (só em Fatura/Recibo) */}
            {docType === 'INVOICE_RECEIPT' && (
              <div className="bg-white border-2 border-slate-100 rounded-3xl p-6">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <FileText size={14} /> Controlo de Faturação
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Estado</div>
                    <select
                      value={form.invoiceStatus}
                      onChange={(e) => onFormChange(prev => ({ 
                        ...prev, 
                        invoiceStatus: e.target.value, 
                        invoiceNumber: e.target.value === 'FATURADO' ? prev.invoiceNumber : '' 
                      }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none"
                    >
                      <option value="NAO_FATURADO">Não Faturar</option>
                      <option value="PARA_FATURAR">Para Faturar</option>
                      <option value="FATURADO">Faturado</option>
                    </select>
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nº Fatura</div>
                    <input
                      value={form.invoiceNumber}
                      onChange={(e) => onFormChange(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                      className="w-full p-4 bg-slate-50 rounded-2xl font-black outline-none"
                      placeholder={form.invoiceStatus === 'FATURADO' ? 'Obrigatório quando faturado' : '—'}
                      disabled={form.invoiceStatus !== 'FATURADO'}
                      required={form.invoiceStatus === 'FATURADO'}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex gap-4 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-4 rounded-2xl font-black bg-slate-100 text-slate-400">
                Cancelar
              </button>
              <button type="submit" className="flex-[2] py-4 rounded-2xl font-black bg-[#1b5e20] text-white hover:bg-green-900 transition-colors shadow-xl">
                {isEditing ? 'Guardar Alterações' : 'Gravar Registo'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

